import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CalendarX, CircleCheck, Pencil, RefreshCw, SearchX, Send, UsersRound } from 'lucide-react';
import SectionEyebrow from './common/SectionEyebrow';
import Loader from './common/Loader';
import ClosedQuestion from './common-questions/SingleChoice';
import OpenQuestion from './common-questions/LongAnswerQuestion';
import RatingQuestion, { ratingScale } from './common-questions/TRating';
import { useLanguage } from '../assets/js/LanguageContext';
import { localeOf } from '../assets/js/locale';
import '../assets/styles/SurveyAnswer.css';

const API = process.env.REACT_APP_API_URL;
const OPEN_TYPES = ['open', 'texto', 'abierta'];

const questionKind = (type) => {
  const normalized = String(type || '').toLowerCase();
  if (OPEN_TYPES.includes(normalized)) return 'open';
  if (normalized === 'closed') return 'closed';
  if (normalized === 'rating') return 'rating';
  return 'unknown';
};

const adaptQuestion = (q) => ({
  id: q.id,
  question: q.pregunta,
  type: q.tipo,
  kind: questionKind(q.tipo),
  options: q.tipo === 'closed' && typeof q.opciones === 'string' ? q.opciones.split(',') : q.opciones || [],
  minValue: q.minValue,
  maxValue: q.maxValue
});

const isAnswered = (question, value) => {
  if (question.kind === 'unknown') return true;
  if (question.kind === 'open') return typeof value === 'string' && value.trim().length > 0;
  return value !== undefined && value !== null && value !== '';
};

const fetchSurvey = async (pin) => {
  const res = await fetch(`${API}/public/survey/${pin}`, { headers: { 'Content-Type': 'application/json' } });
  if (!res.ok) {
    const error = new Error(String(res.status));
    error.status = res.status;
    error.data = await res.json().catch(() => ({}));
    throw error;
  }
  return res.json();
};

const CLOSED_CODES = { survey_closed: 'closed', survey_not_started: 'notstarted', survey_full: 'full' };

const Survey = () => {
  const { t, language } = useLanguage();
  const { pin } = useParams();
  const reduceMotion = useReducedMotion();
  const [status, setStatus] = useState('loading');
  const [survey, setSurvey] = useState({ name: '', id: null });
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [stage, setStage] = useState('questions');
  const [returnToSummary, setReturnToSummary] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [closedInfo, setClosedInfo] = useState(null);
  const headingRef = useRef(null);
  const movedRef = useRef(false);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const data = await fetchSurvey(pin);
      setQuestions((data.questions || []).map(adaptQuestion));
      setSurvey({ name: data.surveyName || '', id: data.idEncuesta });
      setStatus('ready');
    } catch (error) {
      const closedStatus = error.status === 403 && error.data && CLOSED_CODES[error.data.code];
      if (closedStatus) {
        setClosedInfo(error.data);
        setStatus(closedStatus);
        return;
      }
      console.error('Error al cargar el diagnóstico:', error);
      setStatus(error.status === 404 ? 'notfound' : 'error');
    }
  }, [pin]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!movedRef.current) return;
    if (headingRef.current) headingRef.current.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [index, stage, reduceMotion]);

  const total = questions.length;
  const current = questions[index];
  const currentValue = current ? answers[current.id] : undefined;
  const canContinue = current ? isAnswered(current, currentValue) : false;

  const setAnswer = (value) => {
    setAnswers((prev) => ({ ...prev, [current.id]: value }));
  };

  const goTo = (nextIndex, nextStage = 'questions') => {
    movedRef.current = true;
    setDirection(nextIndex >= index ? 1 : -1);
    setIndex(nextIndex);
    setStage(nextStage);
  };

  const handleNext = () => {
    if (!canContinue) return;
    if (returnToSummary || index === total - 1) {
      setReturnToSummary(false);
      movedRef.current = true;
      setDirection(1);
      setStage('summary');
      return;
    }
    goTo(index + 1);
  };

  const handleBack = () => {
    if (index > 0) goTo(index - 1);
  };

  const handleEdit = (questionIndex) => {
    setReturnToSummary(true);
    setSendError(false);
    goTo(questionIndex);
  };

  const handleSubmit = async () => {
    if (sending) return;
    setSending(true);
    setSendError(false);
    const payload = questions
      .filter((q) => q.kind !== 'unknown' && isAnswered(q, answers[q.id]))
      .map((q) => ({
        id_pregunta: q.id,
        id_encuesta: survey.id,
        tipo: q.type,
        respuesta: q.kind === 'rating' ? String(answers[q.id]) : answers[q.id]
      }));

    try {
      let token = localStorage.getItem('token');
      if (!token) {
        const tempRes = await fetch(`${API}/create-temp-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin, purpose: 'send_responses' })
        });
        if (tempRes.status === 403) {
          const data = await tempRes.json().catch(() => ({}));
          setStatus(CLOSED_CODES[data.code] || 'closed');
          return;
        }
        if (!tempRes.ok) throw new Error(`temp-token ${tempRes.status}`);
        token = (await tempRes.json()).token;
      }
      const res = await fetch(`${API}/respuestas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ respuestas: payload })
      });
      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        setStatus(CLOSED_CODES[data.code] || 'closed');
        return;
      }
      if (!res.ok) throw new Error(`respuestas ${res.status}`);
      movedRef.current = true;
      setStage('sent');
    } catch (error) {
      console.error('Error enviando respuestas:', error);
      setSendError(true);
    } finally {
      setSending(false);
    }
  };

  const displayAnswer = (question) => {
    const value = answers[question.id];
    if (question.kind === 'unknown') return t('question_type_not_supported');
    if (!isAnswered(question, value)) return '—';
    if (question.kind === 'rating') return `${value} / ${ratingScale(question).max}`;
    return String(value).trim();
  };

  const hintFor = (question) => {
    if (question.kind === 'open') return t('answer_hint_open');
    if (question.kind === 'closed') return t('answer_hint_closed');
    if (question.kind === 'rating') {
      const { min, max } = ratingScale(question);
      return t('answer_hint_rating').replace('{min}', min).replace('{max}', max);
    }
    return t('question_type_not_supported');
  };

  const renderInput = (question) => {
    const labelledBy = `answer-q-${question.id}`;
    if (question.kind === 'open') {
      return <OpenQuestion question={question} value={currentValue || ''} onChange={setAnswer} label={question.question} />;
    }
    if (question.kind === 'closed') {
      return <ClosedQuestion question={question} value={currentValue} onChange={setAnswer} labelledBy={labelledBy} />;
    }
    if (question.kind === 'rating') {
      return <RatingQuestion question={question} value={currentValue} onChange={setAnswer} labelledBy={labelledBy} />;
    }
    return null;
  };

  const slide = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, x: 28 * direction },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -28 * direction }
      };

  if (status === 'loading') {
    return (
      <main className="tp-answer">
        <div className="tp-answer__inner tp-answer__inner--center">
          <Loader />
        </div>
      </main>
    );
  }

  if (status === 'full') {
    return (
      <main className="tp-answer">
        <div className="tp-answer__inner tp-answer__inner--center">
          <section className="tp-answer__card tp-answer__state">
            <span className="tp-answer__state-icon" aria-hidden="true">
              <UsersRound size={28} strokeWidth={1.75} />
            </span>
            {closedInfo && closedInfo.surveyName && <p className="tp-answer__qnum">{closedInfo.surveyName}</p>}
            <h1 className="tp-answer__question">{t('answer_full_title')}</h1>
            <p className="tp-answer__hint">{t('answer_full_text')}</p>
            <Link to="/pin" className="btn btn--primary">{t('answer_try_other')}</Link>
          </section>
        </div>
      </main>
    );
  }

  if (status === 'closed' || status === 'notstarted') {
    const formatDate = (value) => {
      const date = value ? new Date(value) : null;
      return date && !Number.isNaN(date.getTime())
        ? date.toLocaleDateString(localeOf(language), { day: 'numeric', month: 'long', year: 'numeric' })
        : '';
    };
    const notStarted = status === 'notstarted';
    const date = formatDate(closedInfo && (notStarted ? closedInfo.fechaInicio : closedInfo.fechaFin));
    const textKey = notStarted ? 'answer_not_started_text' : date ? 'answer_closed_text' : 'answer_closed_text_nodate';
    return (
      <main className="tp-answer">
        <div className="tp-answer__inner tp-answer__inner--center">
          <section className="tp-answer__card tp-answer__state">
            <span className="tp-answer__state-icon" aria-hidden="true">
              <CalendarX size={28} strokeWidth={1.75} />
            </span>
            {closedInfo && closedInfo.surveyName && <p className="tp-answer__qnum">{closedInfo.surveyName}</p>}
            <h1 className="tp-answer__question">{t(notStarted ? 'answer_not_started_title' : 'answer_closed_title')}</h1>
            <p className="tp-answer__hint">{t(textKey).replace('{date}', date)}</p>
            <Link to="/pin" className="btn btn--primary">{t('answer_try_other')}</Link>
          </section>
        </div>
      </main>
    );
  }

  if (status !== 'ready' || total === 0) {
    const notFound = status === 'notfound';
    return (
      <main className="tp-answer">
        <div className="tp-answer__inner tp-answer__inner--center">
          <section className="tp-answer__card tp-answer__state">
            <span className="tp-answer__state-icon" aria-hidden="true">
              {notFound ? <SearchX size={28} strokeWidth={1.75} /> : <RefreshCw size={28} strokeWidth={1.75} />}
            </span>
            <h1 className="tp-answer__question">
              {notFound ? t('answer_not_found_title') : status === 'error' ? t('answer_load_error_title') : t('answer_empty_title')}
            </h1>
            <p className="tp-answer__hint">
              {notFound ? t('answer_not_found_text').replace('{pin}', pin) : status === 'error' ? t('answer_load_error_text') : t('answer_empty_text')}
            </p>
            {status === 'error' ? (
              <button type="button" className="btn btn--primary" onClick={load}>
                <RefreshCw size={16} strokeWidth={2} aria-hidden="true" />
                {t('sd_retry')}
              </button>
            ) : (
              <Link to="/pin" className="btn btn--primary">{t('answer_try_other')}</Link>
            )}
          </section>
        </div>
      </main>
    );
  }

  const progress = stage === 'questions' ? ((index + 1) / (total + 1)) * 100 : 100;

  return (
    <main className="tp-answer">
      <div className="tp-answer__inner">
        <header className="tp-answer__head">
          <SectionEyebrow>{t('answer_eyebrow')}</SectionEyebrow>
          <h1 className="tp-answer__title">{survey.name}</h1>
          {stage !== 'sent' && (
            <div className="tp-answer__progress">
              <p className="tp-answer__progress-text">
                {stage === 'questions'
                  ? t('answer_progress').replace('{n}', index + 1).replace('{total}', total)
                  : t('answer_progress_review')}
              </p>
              <span
                className="tp-answer__bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress)}
                aria-label={t('answer_progress_label')}
              >
                <span style={{ width: `${progress}%` }} />
              </span>
            </div>
          )}
        </header>

        <AnimatePresence mode="wait" initial={false}>
          {stage === 'questions' && current && (
            <motion.section
              key={`q-${current.id}`}
              className="tp-answer__card"
              {...slide}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <p className="tp-answer__qnum">{t('sd_question_n').replace('{n}', index + 1)}</p>
              <h2 id={`answer-q-${current.id}`} ref={headingRef} tabIndex={-1} className="tp-answer__question">
                {current.question}
              </h2>
              <p className="tp-answer__hint">{hintFor(current)}</p>

              {renderInput(current)}

              <footer className="tp-answer__nav">
                {index > 0 && !returnToSummary ? (
                  <button type="button" className="btn btn--ghost tp-answer__back" onClick={handleBack}>
                    <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
                    {t('common_back')}
                  </button>
                ) : <span />}
                <button type="button" className="btn btn--primary tp-answer__next" onClick={handleNext} disabled={!canContinue}>
                  {returnToSummary ? t('answer_back_to_summary') : index === total - 1 ? t('answer_finish') : t('common_next')}
                  <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
                </button>
              </footer>
            </motion.section>
          )}

          {stage === 'summary' && (
            <motion.section
              key="summary"
              className="tp-answer__card"
              {...slide}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <h2 ref={headingRef} tabIndex={-1} className="tp-answer__question">{t('answer_summary_title')}</h2>
              <p className="tp-answer__hint">{t('answer_summary_text')}</p>

              <ol className="tp-answer__summary">
                {questions.map((q, i) => (
                  <li key={q.id} className="tp-answer__summary-item">
                    <span className="tp-answer__summary-num" aria-hidden="true">{i + 1}</span>
                    <div className="tp-answer__summary-body">
                      <p className="tp-answer__summary-q">{q.question}</p>
                      <p className="tp-answer__summary-a">{displayAnswer(q)}</p>
                    </div>
                    {q.kind !== 'unknown' && (
                      <button
                        type="button"
                        className="tp-answer__edit"
                        onClick={() => handleEdit(i)}
                        aria-label={`${t('answer_change')}: ${q.question}`}
                      >
                        <Pencil size={14} strokeWidth={2} aria-hidden="true" />
                        {t('answer_change')}
                      </button>
                    )}
                  </li>
                ))}
              </ol>

              {sendError && <p className="tp-answer__alert" role="alert">{t('answer_send_error')}</p>}

              <footer className="tp-answer__nav">
                <span />
                <button type="button" className="btn btn--primary tp-answer__next" onClick={handleSubmit} disabled={sending}>
                  <Send size={16} strokeWidth={2} aria-hidden="true" />
                  {sending ? t('answer_sending') : t('survey_submit_answers')}
                </button>
              </footer>
            </motion.section>
          )}

          {stage === 'sent' && (
            <motion.section
              key="sent"
              className="tp-answer__card tp-answer__state"
              {...slide}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <span className="tp-answer__state-icon is-success" aria-hidden="true">
                <CircleCheck size={32} strokeWidth={1.75} />
              </span>
              <h2 ref={headingRef} tabIndex={-1} className="tp-answer__question">{t('survey_thanks_participating')}</h2>
              <p className="tp-answer__hint">{t('answer_sent_text')}</p>
              <Link to="/" className="btn btn--outline">{t('common_back_home')}</Link>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
};

export default Survey;
